import React, { useState, useEffect, useContext, Suspense, useCallback } from "react";
import { useParams, useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import { useTheme, useMediaQuery, Typography } from "@material-ui/core";
import Button from "@material-ui/core/Button";
import Box from "@material-ui/core/Box";
import BottomNavigation from "@material-ui/core/BottomNavigation";
import BottomNavigationAction from "@material-ui/core/BottomNavigationAction";
import ChatIcon from "@material-ui/icons/Chat";
import QuestionAnswerIcon from "@material-ui/icons/QuestionAnswer";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import CircularProgress from "@material-ui/core/CircularProgress";

import TicketsManagerTabs from "../../components/TicketsManagerTabs/";
import TicketAdvancedLayout from "../../components/TicketAdvancedLayout";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import { QueueSelectedProvider } from "../../context/QueuesSelected/QueuesSelectedContext";

const Ticket = React.lazy(() => import("../../components/Ticket/"));

const desktopTabCycle = ["open", "pending", "group"];
const tabLabels = { open: "Atendendo", pending: "Aguardando", group: "Grupos" };

const useStyles = makeStyles((theme) => ({
  header: {},
  content: {
    overflowY: "auto",
    overflowX: "hidden",
    minHeight: 0,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    "@media (max-width: 960px)": {
      flex: 1,
      minHeight: 0,
      height: "100%",
      maxHeight: "100%",
      overflow: "hidden",
    },
  },
  placeholderContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    background: theme.palette.tabHeaderBackground,
  },
  placeholderItem: {},
  bottomNavAction: {
    minWidth: 60,
    "&.Mui-selected": {
      color: theme.palette.primary.main,
    },
  },
  subTabLabel: {
    fontSize: 9,
    fontWeight: 600,
    color: theme.palette.primary.main,
    backgroundColor: `${theme.palette.primary.main}20`,
    borderRadius: 8,
    padding: "1px 6px",
    marginTop: 2,
  },
  mobileFooter: {
    display: "none",
    [theme.breakpoints.down("md")]: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      padding: theme.spacing(0.5, 0, 0),
      borderTop: `1px solid ${theme.palette.divider}`,
      background: theme.palette.background.paper,
      position: "fixed",
      bottom: 0,
      left: 0,
      width: "100%",
      zIndex: 1100,
    },
  },
  mobileWhatsAppIcon: {
    color: theme.palette.primary.main,
    fontSize: 28,
  },
  mobileAtendimentosLabel: {
    fontSize: 10,
    fontWeight: 600,
    color: theme.mode === "dark" ? "#ccc" : "#555",
  },
}));

const TicketAdvanced = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { ticketId } = useParams();
  const history = useHistory();
  const [option, setOption] = useState(0);
  const { user } = useContext(AuthContext);
  const { currentTicket, setCurrentTicket, tabOpen, setTabOpen } = useContext(TicketsContext);
  const [subTabIndex, setSubTabIndex] = useState(0);
  const tabCycle = user?.allowGroup ? ["open", "pending", "group"] : ["open", "pending"];

  useEffect(() => {
    if (currentTicket.id !== null) {
      setCurrentTicket({ id: currentTicket.id, code: "#open" });
    }
    if (!ticketId) {
      setOption(1);
    }
    return () => {
      setCurrentTicket({ id: null, code: null });
    };
  }, []);

  useEffect(() => {
    if (currentTicket.id !== null) {
      setOption(0);
    }
  }, [currentTicket]);

  useEffect(() => {
    const idx = tabCycle.indexOf(tabOpen);
    if (idx !== -1) {
      setSubTabIndex(idx);
    }
  }, [tabCycle, tabOpen]);

  const handleAtendimentosClick = useCallback(() => {
    const currentIndex = tabCycle.indexOf(tabOpen);
    const nextIndex = ((currentIndex === -1 ? 0 : currentIndex) + 1) % tabCycle.length;

    if (isMobile) {
      if (ticketId) {
        history.push("/tickets");
      }
      setSubTabIndex(nextIndex);
      setTabOpen(tabCycle[nextIndex]);
    } else {
      if (option === 1) {
        setSubTabIndex(nextIndex);
        setTabOpen(tabCycle[nextIndex]);
      } else {
        setOption(1);
      }
    }
  }, [isMobile, option, tabCycle, tabOpen, setTabOpen, ticketId, history]);

  const renderPlaceholder = () => (
    <Box className={classes.placeholderContainer}>
      <div className={classes.placeholderItem}>{i18n.t("chat.noTicketMessage")}</div>
      <br />
      <Button onClick={() => setOption(1)} variant="contained" color="primary">
        Selecionar Ticket
      </Button>
    </Box>
  );

  const renderMessageContext = () => {
    if (ticketId && ticketId !== "undefined") {
      return (
        <Suspense fallback={<Box display="flex" justifyContent="center" alignItems="center" height="100%"><CircularProgress /></Box>}>
          <Ticket />
        </Suspense>
      );
    }
    return renderPlaceholder();
  };

  const renderTicketsManagerTabs = () => <TicketsManagerTabs />;

  const renderMobileFooter = () => (
    <Box className={classes.mobileFooter} onClick={handleAtendimentosClick} style={{ cursor: "pointer" }}>
      <WhatsAppIcon className={classes.mobileWhatsAppIcon} />
      <span className={classes.subTabLabel}>{tabLabels[tabCycle[subTabIndex]]}</span>
    </Box>
  );

  const renderDesktopHeader = () => (
    <BottomNavigation
      value={option}
      onChange={(event, newValue) => {
        if (newValue === 1) {
          handleAtendimentosClick();
        } else {
          setOption(0);
        }
      }}
      showLabels
    >
      <BottomNavigationAction
        label="Ticket"
        icon={<ChatIcon />}
        className={classes.bottomNavAction}
      />
      <BottomNavigationAction
        label={
          <Box display="flex" flexDirection="column" alignItems="center">
            <span>Atendimentos</span>
            {option === 1 && (
              <span className={classes.subTabLabel}>{tabLabels[tabCycle[subTabIndex]]}</span>
            )}
          </Box>
        }
        icon={<QuestionAnswerIcon />}
        className={classes.bottomNavAction}
      />
    </BottomNavigation>
  );

  return (
    <QueueSelectedProvider>
      <TicketAdvancedLayout hasTicket={!!ticketId}>
        {!isMobile && <Box className={classes.header}>{renderDesktopHeader()}</Box>}
        <Box className={classes.content} style={isMobile ? (ticketId ? { overflow: "hidden", paddingBottom: 0, flex: 1, minHeight: 0, height: "100%", maxHeight: "100%" } : {}) : undefined}>
         {isMobile
           ? (ticketId ? renderMessageContext() : renderTicketsManagerTabs())
           : option === 0 ? renderMessageContext() : renderTicketsManagerTabs()}
        </Box>
        {isMobile && !ticketId && renderMobileFooter()}
      </TicketAdvancedLayout>
    </QueueSelectedProvider>
  );
};

export default TicketAdvanced;
